import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft, Home, BookOpen, User, Menu as MenuIcon, X as XIcon, LogOut, ChevronDown, UserCircle, ListTodo, Sun, Moon, Mail } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useGeneration } from '../context/GenerationContext';
import { useMailContext } from '../context/MailContext';
import { useTasks } from '../hooks/useTasks';
import { GenerationTask } from '../types/generation';

interface NavigationMenuProps {
  onBack?: () => void;
  onMenuStateChange?: (isOpen: boolean) => void;
  isMenuActuallyOpen?: boolean;
}

export const NavigationMenu = React.memo(function NavigationMenu({ onBack, onMenuStateChange, isMenuActuallyOpen }: NavigationMenuProps) {
  const customNavigate = useCustomNavigate();
  const reactRouterNavigate = useNavigate(); // Используем стандартный useNavigate для UserMenu логики
  const location = useLocation();
  const { isAuthenticated, profile, logout } = useAuth();
  const { markAllTasksAsViewed } = useGeneration();
  const { theme, toggleTheme } = useTheme();
  const mailContext = useMailContext();
  
  // Используем новый хук для получения задач
  const { data: tasksData } = useTasks();
  const tasks = tasksData?.tasks || [];
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);

  // --- Мемоизируем вычисления для значка задач ---
  const taskCounters = useMemo(() => {
    // 1. Считаем непросмотренные завершенные задачи (исключая pending)
    const unviewedCompletedTasks = tasks.filter((task: GenerationTask) => !task.isViewed && task.status !== 'pending');
    const unviewedCompletedTaskCount = unviewedCompletedTasks.length;
    
    // 2. Считаем задачи в процессе (для анимации)
    const pendingTasks = tasks.filter((task: GenerationTask) => task.status === 'pending');
    const pendingTaskCount = pendingTasks.length;
    const hasInProgressTasks = pendingTaskCount > 0;
    
    // 3. Общий счетчик для отображения (завершенные + в процессе)
    const totalTaskCount = unviewedCompletedTaskCount + pendingTaskCount;
    const displayTaskCount = Math.min(totalTaskCount, 99); // Максимум 99 для отображения
    
    return {
      unviewedCompletedTaskCount,
      pendingTaskCount,
      totalTaskCount,
      displayTaskCount,
      hasInProgressTasks
    };
  }, [tasks]);
  
  // Извлекаем значения из мемоизированного объекта
  const { totalTaskCount, displayTaskCount, hasInProgressTasks } = taskCounters;
  // --- Конец мемоизации ---

  const unreadMailCount = mailContext?.unreadCount ?? 0;

  // Проверяем, находимся ли мы на странице авторизации или базы знаний
  const isAuthPage = location.pathname.startsWith('/auth/');
  const isKnowledgePage = location.pathname.startsWith('/knowledge');
  const isStudioPage = location.pathname.startsWith('/studio');

  // Функция для перехода на главную с генерацией ID
  const navigateToHome = () => {
    customNavigate('/studio');
  };

  // Функция для перехода в базу знаний
  const navigateToKnowledge = () => {
    customNavigate('/knowledge/root');
  };



  // Определяем текущий режим отображения (для скрытия/отображения элементов)
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('desktop');
  React.useEffect(() => {
    const checkViewMode = () => {
      if (window.innerWidth < 1024) {
        setViewMode('mobile');
      } else {
        setViewMode('desktop');
        if (onMenuStateChange && isMenuActuallyOpen === false) {
          onMenuStateChange(true);
        }
      }
    };
    checkViewMode();
    window.addEventListener('resize', checkViewMode);
    return () => window.removeEventListener('resize', checkViewMode);
  }, [onMenuStateChange, isMenuActuallyOpen]);

  const handleToggleMenu = () => {
    if (onMenuStateChange) {
      onMenuStateChange(!isMenuActuallyOpen);
    }
  };

  // Логика из UserMenu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        userMenuButtonRef.current &&
        !userMenuButtonRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      reactRouterNavigate('/auth/login');
    } catch (error) {
      console.error('Error logging out:', error);
      if (error instanceof Error) {
      } else {
      }
    }
  };

  const getMenuPosition = () => {
    if (!userMenuButtonRef.current) return { top: 0, right: 0 };
    const rect = userMenuButtonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY,
      right: window.innerWidth - rect.right - window.scrollX
    };
  };

  const menuPosition = getMenuPosition();

  return (
    <div className="flex items-center justify-between p-5 ">
      <div className="flex items-center space-x-2 lg:space-x-8">
        {isKnowledgePage && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleToggleMenu();
            }}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 lg:block xl:hidden menu-hamburger-button"
            aria-label={isMenuActuallyOpen ? "Закрыть меню" : "Открыть меню"}
          >
            {isMenuActuallyOpen ? <XIcon className="w-6 h-6 text-blue-500" /> : <MenuIcon className="w-6 h-6 text-blue-500" />}
          </button>
        )}
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Назад
          </button>
        )}
        {!isAuthPage && !isStudioPage && (
          <>
            <button
              onClick={navigateToHome}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-md"
            >
              <Home className="w-5 h-5 lg:mr-2" />
              <span className="hidden lg:inline">Главная</span>
            </button>
          </>
        )}
        {!isAuthPage && !isKnowledgePage && !isStudioPage && (
          <>
            <button
              onClick={navigateToKnowledge}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-md"
            >
              <BookOpen className="w-5 h-5 lg:mr-2" />
              <span className="hidden lg:inline">База знаний</span>
            </button>

          </>
        )}
      </div>
      <div className="flex items-center space-x-2 lg:space-x-4">
        {!isAuthPage && isAuthenticated && (
          <>
            <button
              onClick={() => {
                // Сначала мгновенно переходим на страницу задач
                reactRouterNavigate('/tasks');
                // Затем выполняем пометку задач в фоне
                markAllTasksAsViewed().catch(error => {
                  console.error('Ошибка при пометке всех задач как просмотренных:', error);
                });
              }}
              className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="История"
            >
              {/* --- Мобильный вид --- */}
              <div className="sm:hidden relative flex items-center justify-center w-5 h-5">
                <ListTodo className="w-full h-full text-gray-700 dark:text-gray-300" />
                {totalTaskCount > 0 && (
                  <div className="absolute -top-3 -right-3 w-4 h-4 z-10">
                    {/* Анимированная рамка ВОКРУГ кружка (только мобильные, если есть задачи в процессе) */}
                    {hasInProgressTasks && (
                      // Рамка должна быть "выше" кружка, теперь синяя
                      <div className="absolute -inset-0.5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin z-20"></div>
                    )}
                    {/* Синий кружок с белой цифрой. Он будет под рамкой, если z-index рамки выше */}
                    <span className="absolute inset-0 bg-blue-600 text-white text-xs leading-none flex items-center justify-center rounded-full z-10">
                      {displayTaskCount}
                    </span>
                  </div>
                )}
              </div>

              {/* --- Десктопный вид --- */}
              <div className="hidden sm:flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <span className="text-gray-700 dark:text-gray-300">История</span>
              </div>

              {/* --- Внешний значок для десктопа (без анимации рамки) --- */}
              {totalTaskCount > 0 && (
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 hidden sm:flex items-center justify-center z-10 bg-blue-600 text-white text-xs rounded-full">
                  {displayTaskCount}
                </div>
              )}
            </button>

            <button
              onClick={() => reactRouterNavigate('/mail')}
              className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Почта"
            >
              {/* --- Мобильный вид --- */}
              <div className="sm:hidden relative flex items-center justify-center w-5 h-5">
                <Mail className="w-full h-full text-gray-700 dark:text-gray-300" />
                {unreadMailCount > 0 && (
                  <div className="absolute -top-3 -right-3 w-4 h-4 z-10">
                    {/* Синий кружок с белой цифрой */}
                    <span className="absolute inset-0 bg-blue-600 text-white text-xs leading-none flex items-center justify-center rounded-full z-10">
                      {Math.min(unreadMailCount, 9)}
                    </span>
                  </div>
                )}
              </div>

              {/* --- Десктопный вид --- */}
              <div className="hidden sm:flex items-center gap-2">
                <Mail className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <span className="text-gray-700 dark:text-gray-300">Почта</span>
              </div>

              {/* --- Внешний значок для десктопа (без анимации рамки) --- */}
              {unreadMailCount > 0 && (
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 hidden sm:flex items-center justify-center z-10 bg-blue-600 text-white text-xs rounded-full">
                  {Math.min(unreadMailCount, 9)}
                </div>
              )}
            </button>

            <button
              ref={userMenuButtonRef}
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>

              {profile && (
                <div className="sm:hidden ml-2">
                  <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isUserMenuOpen ? 'transform rotate-180' : ''}`} />
                </div>
              )}

              {profile && (
                <div className="hidden sm:flex items-center space-x-2 ml-2">
                  <span className="text-gray-700 dark:text-gray-300">{profile.nickname}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isUserMenuOpen ? 'transform rotate-180' : ''}`} />
                </div>
              )}
            </button>

            {isUserMenuOpen && createPortal(
              <div
                ref={userMenuRef}
                className="fixed z-[9999]"
                style={{
                  top: `${menuPosition.top}px`,
                  right: `${menuPosition.right}px`,
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="w-48 bg-white dark:bg-gray-800 rounded-lg py-2 mt-2 shadow-lg"
                >
                  {profile && (
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {profile.nickname}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {profile.email}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      reactRouterNavigate('/profile');
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <UserCircle className="w-4 h-4 mr-2" />
                    Личный кабинет
                  </button>
                  <button
                    onClick={toggleTheme}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {theme === 'light' ? (
                      <Moon className="w-4 h-4 mr-2 text-blue-500" />
                    ) : (
                      <Sun className="w-4 h-4 mr-2 text-yellow-500" />
                    )}
                    Сменить тему
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Выйти
                  </button>
                </motion.div>
              </div>,
              document.body
            )}
          </>
        )}
      </div>
    </div>
  );
});
